import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Loader2, Trophy, Users, UserCheck, ArrowRight } from 'lucide-react';
import sessionService from '@/services/sessionService.ts';
import teamService from '@/services/teamService.ts';
import TeamGrid from '@/components/admin/TeamGrid.tsx';
import TeamRankingGrid from '@/components/admin/TeamRankingGrid.tsx';
import { useAuth } from '@/context/AuthContext.tsx';
import TeamSelector from '@/components/ui/TeamSelector.tsx';
import LegalNoticeBanner from '@/components/shared/LegalNoticeBanner.tsx';
import { useAsyncData } from '@/hooks/useAsyncData.ts';
import { Button } from '@/components/ui/button';

import type { SalespersonStats } from '@/types/session.ts';

interface RankingTabProps {
  onSelectMember?: (member: SalespersonStats) => void;
}

type RankingView = 'teams' | 'vendors';

export default function RankingTab({ onSelectMember }: RankingTabProps) {
  const { t } = useTranslation();
  const { user, isSalesManager } = useAuth();

  const [view, setView] = useState<RankingView>(isSalesManager ? 'vendors' : 'teams');
  const [selectedTeamId, setSelectedTeamId] = useState<string>('');

  // Primary data: team rankings + (if manager) own teams list
  const { data: primary, loading, error } = useAsyncData(
    async () => {
      const teamRankings = await sessionService.getTeamRankings();
      const teams = isSalesManager ? await teamService.getMyTeams() : [];
      return { teamRankings, teams };
    },
    [isSalesManager],
  );

  const teamRankings = primary?.teamRankings;
  const teams = primary?.teams ?? [];

  // Auto-select the first team when teams list arrives (manager only)
  useEffect(() => {
    if (teams.length > 0 && !selectedTeamId) setSelectedTeamId(teams[0].id);
  }, [teams, selectedTeamId]);

  const isAllView = selectedTeamId === 'all';
  const statsTeamId = isSalesManager
    ? (isAllView ? undefined : selectedTeamId)
    : user?.teamId;
  const vendorStatsEnabled =
    view === 'vendors' && (isAllView || !!statsTeamId);

  // Secondary data: team stats for vendor view (only when enabled)
  const { data: teamStats } = useAsyncData(
    () => sessionService.getTeamStats(statsTeamId),
    [statsTeamId, isAllView],
    { enabled: vendorStatsEnabled, fallbackError: '' },
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-4xl rounded-xl border border-border bg-card p-8 text-center">
        <p className="text-sm text-red-500">{t('ranking.errorLoading', { error })}</p>
      </div>
    );
  }

  // Empty state for a brand-new Manager with no teams yet — prevents the vendor
  // tab from sitting on a permanent spinner because team-stats fetch is gated on
  // a selected teamId that will never appear.
  if (isSalesManager && teams.length === 0) {
    return (
      <div className="mx-auto max-w-2xl rounded-xl border border-border bg-card p-10 text-center">
        <Trophy className="mx-auto mb-4 h-10 w-10 text-muted-foreground" />
        <h2 className="text-xl font-heading text-foreground">
          {t('ranking.empty.managerTitle', { defaultValue: 'Aún no tienes equipos' })}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {t('ranking.empty.managerDescription', {
            defaultValue: 'Crea un equipo y añade vendedores para ver su rendimiento aquí.',
          })}
        </p>
        <Button asChild className="mt-6">
          <Link to="/team">
            {t('ranking.empty.managerCta', { defaultValue: 'Ir a Equipos' })}
            <ArrowRight className="size-4" />
          </Link>
        </Button>
      </div>
    );
  }

  const teamCount = teamRankings?.teams.length ?? 0;
  const memberCount = teamStats?.salespersonStats.length ?? 0;
  const currentTeamId = user?.teamId ?? undefined;
  const selectedTeamName = teams.find((t) => t.id === selectedTeamId)?.name;

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <Trophy className="h-5 w-5 text-yellow-500" />
        <h1 className="text-2xl font-heading text-foreground">
          {t('ranking.title')}
        </h1>
        <span className="text-base font-normal text-muted-foreground">
          · {view === 'teams'
            ? t('ranking.teamCount', { count: teamCount })
            : t('ranking.vendorCount', { count: memberCount })}
        </span>

        {/* Toggle for manager */}
        {isSalesManager && (
          <div className="ml-auto flex rounded-lg border border-border bg-muted p-0.5">
            <button
              onClick={() => setView('vendors')}
              className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                view === 'vendors'
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <UserCheck className="h-3.5 w-3.5" />
              {t('ranking.vendorsView')}
            </button>
            <button
              onClick={() => setView('teams')}
              className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                view === 'teams'
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Users className="h-3.5 w-3.5" />
              {t('ranking.teamsView')}
            </button>
          </div>
        )}
      </div>

      {/* Team selector for manager's vendor view */}
      {isSalesManager && view === 'vendors' && teams.length > 1 && (
        <div className="mb-5">
          <TeamSelector
            value={selectedTeamId}
            onChange={setSelectedTeamId}
            label={t('ranking.filterByTeam')}
            options={[
              ...teams.map((team) => ({ value: team.id, label: team.name })),
              { value: 'all', label: t('ranking.viewAll') },
            ]}
          />
        </div>
      )}

      {isSalesManager && (
        <LegalNoticeBanner
          storageKey="disclaimer-manager-dismissed"
          messageKey="legal.managerDisclaimer"
          learnMoreLink="/data-privacy"
        />
      )}

      {/* Content */}
      {view === 'teams' ? (
        <TeamRankingGrid
          teams={teamRankings?.teams ?? []}
          currentTeamId={currentTeamId}
        />
      ) : (
        <>
          {isSalesManager && (isAllView || selectedTeamName) && (
            <p className="mb-4 text-sm text-muted-foreground">
              {isAllView
                ? t('ranking.showingAll')
                : t('ranking.showingTeam', { team: selectedTeamName })}
            </p>
          )}
          {teamStats ? (
            teamStats.salespersonStats.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border p-8 text-center">
                <p className="text-sm text-muted-foreground">
                  {t('ranking.empty.teamNoSalespeople', {
                    defaultValue: 'Este equipo todavía no tiene vendedores. Añade miembros desde Equipos.',
                  })}
                </p>
              </div>
            ) : (
              <TeamGrid
                salespersonStats={teamStats.salespersonStats}
                onSelectMember={onSelectMember}
                currentUserId={user?.id}
              />
            )
          ) : (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          )}
        </>
      )}
    </div>
  );
}
